import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "@/components/Reveal";
import Monogram from "@/components/Monogram";
import api from "@/lib/api";
import { toast } from "sonner";
import { SERVICES, SERVICE_TIMES } from "@/content/site";

export default function Booking() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: SERVICES[0].title,
    preferred_date: "",
    preferred_time: SERVICE_TIMES[0],
    notes: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.preferred_date) {
      toast.error("Please share your name, phone and a preferred date.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", form);
      toast.success("Your appointment request has been received.");
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="pt-44 pb-12 px-6 lg:px-14 bg-[#F5F2ED] relative overflow-hidden">
        <Monogram size={680} opacity={0.05} color="#4A3E39" className="absolute -right-60 top-10 hidden md:block" />
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="eyebrow mb-6">Book an Appointment</div>
            <h1 className="font-display font-light text-ivory-800 text-6xl md:text-8xl lg:text-[110px] leading-[0.95] max-w-5xl">
              Reserve a <em className="italic">morning</em>.
            </h1>
            <p className="mt-10 max-w-xl text-[15px] leading-[1.9] text-ivory-700">
              Share a few details. We will write back within twenty-four hours with available
              times and a short note from the artist assigned to you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-6 lg:px-14 pb-32 bg-[#F5F2ED]">
        <form
          onSubmit={onSubmit}
          data-testid="booking-form"
          className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20"
        >
          <Reveal className="lg:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
              <div>
                <label className="eyebrow mb-3 block">Name</label>
                <input
                  data-testid="booking-name"
                  className="editorial-input"
                  type="text"
                  placeholder="Your full name"
                  value={form.name}
                  onChange={update("name")}
                  required
                />
              </div>
              <div>
                <label className="eyebrow mb-3 block">Phone</label>
                <input
                  data-testid="booking-phone"
                  className="editorial-input"
                  type="tel"
                  placeholder="+91"
                  value={form.phone}
                  onChange={update("phone")}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="eyebrow mb-3 block">Email (optional)</label>
                <input
                  data-testid="booking-email"
                  className="editorial-input"
                  type="email"
                  placeholder="you@studio.com"
                  value={form.email}
                  onChange={update("email")}
                />
              </div>
              <div>
                <label className="eyebrow mb-3 block">Service</label>
                <select
                  data-testid="booking-service"
                  className="editorial-input bg-transparent"
                  value={form.service}
                  onChange={update("service")}
                >
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.title}>{s.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="eyebrow mb-3 block">Preferred date</label>
                <input
                  data-testid="booking-date"
                  className="editorial-input"
                  type="date"
                  value={form.preferred_date}
                  onChange={update("preferred_date")}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="eyebrow mb-3 block">Preferred time</label>
                <div className="flex flex-wrap gap-3">
                  {SERVICE_TIMES.map((t) => (
                    <button
                      type="button"
                      key={t}
                      data-testid={`booking-time-${t.toLowerCase()}`}
                      onClick={() => setForm((f) => ({ ...f, preferred_time: t }))}
                      className={`text-[11px] tracking-[0.28em] uppercase px-5 py-3 border transition-colors ${
                        form.preferred_time === t
                          ? "bg-[#2C2724] text-ivory-100 border-[#2C2724]"
                          : "border-[#B7AB9B] text-ivory-700 hover:border-[#2C2724]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="eyebrow mb-3 block">Anything we should know? (optional)</label>
                <textarea
                  data-testid="booking-notes"
                  className="editorial-input min-h-[120px] resize-none"
                  placeholder="Hair history, allergies, occasion…"
                  value={form.notes}
                  onChange={update("notes")}
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="booking-submit"
              className="btn-burgundy mt-12 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Request Appointment"}
            </button>
          </Reveal>

          <Reveal className="lg:col-span-5 lg:pl-10" delay={120}>
            <div className="zoom-wrap aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1612196808827-9ff25cb6137a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxuZXV0cmFsJTIwaW50ZXJpb3IlMjBtaW5pbWFsJTIwc3VubGlnaHR8ZW58MHx8fHwxNzgyMTE1ODg5fDA&ixlib=rb-4.1.0&q=85"
                alt="The atelier"
                className="zoom-img w-full h-full object-cover"
              />
            </div>
            <div className="mt-8 space-y-2 text-[14px] leading-[1.8] text-ivory-700">
              <p>14, Pali Hill Road · Bandra West, Mumbai</p>
              <p>Tuesday — Saturday · 10–8</p>
              <p>hello@tsivory.com · +91 22 4040 8080</p>
            </div>
          </Reveal>
        </form>
      </section>
    </>
  );
}
